import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, DownloadCloud, RefreshCw, Loader2, ArrowUp, ArrowDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { usePanel } from "./PanelLayout";
import { rm, errMsg, timeMY } from "@/lib/api";
import { MenuFormDialog } from "@/components/staff/MenuFormDialog";

function LoyverseBar({ req, onDone }) {
  const [busy, setBusy] = useState("");
  const [last, setLast] = useState(null);
  const [auto, setAuto] = useState(false);
  useEffect(() => { req("get", "/staff/settings").then((s) => { setLast(s.loyverse_last_sync || ""); setAuto(s.loyverse_auto_sync !== false); }).catch(() => {}); }, [req]);
  const toggleAuto = async () => {
    try {
      const r = await req("put", "/staff/loyverse/auto-sync", { enabled: !auto });
      setAuto(r.loyverse_auto_sync);
      toast.success(r.loyverse_auto_sync ? "Auto segerak Loyverse DIHIDUPKAN" : "Auto segerak Loyverse DIMATIKAN");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const run = async (kind) => {
    if (kind === "import" && !window.confirm("Import semua item dari Loyverse? Menu yang sama nama akan dikemaskini.")) return;
    setBusy(kind);
    try {
      const r = await req("post", kind === "import" ? "/staff/loyverse/import-menu" : "/staff/loyverse/sync");
      setLast(r.last_sync);
      toast.success(kind === "import" ? `Import siap: ${r.created} baharu, ${r.updated} dikemaskini${r.skipped ? `, ${r.skipped} dilangkau` : ""}` : `Segerak siap: ${r.updated} menu, ${r.unavailable} habis`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const btn = "flex items-center gap-2 px-4 h-10 rounded-full border text-sm font-semibold active:scale-95 transition-[transform,background-color] disabled:opacity-60";
  return (
    <div className="flex flex-wrap items-center gap-2 mb-5 p-3 rounded-2xl bg-white border" data-testid="loyverse-bar">
      <button disabled={!!busy} onClick={() => run("import")} data-testid="loyverse-import-button" className={`${btn} border-[#B91C1C] text-[#B91C1C] hover:bg-red-50`}>
        {busy === "import" ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}Import Menu dari Loyverse
      </button>
      <button disabled={!!busy} onClick={() => run("sync")} data-testid="loyverse-sync-button" className={`${btn} hover:bg-slate-50`}>
        <RefreshCw className={`w-4 h-4 ${busy === "sync" ? "animate-spin" : ""}`} />Segerak Harga & Stok
      </button>
      <button onClick={toggleAuto} data-testid="loyverse-auto-toggle" className={`${btn} ${auto ? "border-green-600 bg-green-50 text-green-800" : "border-red-300 bg-red-50 text-red-700"}`}>
        <span className={`w-2.5 h-2.5 rounded-full ${auto ? "bg-green-600" : "bg-red-500"}`} />Auto Segerak: {auto ? "HIDUP" : "MATI"}
      </button>
      <span className="text-xs text-slate-500 ml-auto" data-testid="loyverse-last-sync">
        {last ? `Segerak terakhir: ${new Date(last).toLocaleDateString("ms-MY")} ${timeMY(last)}${auto ? " · auto setiap 15 minit" : " · auto dimatikan"}` : "Belum pernah segerak"}
      </span>
    </div>
  );
}

export default function MenuAdmin() {
  const { req, me } = usePanel();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [variants, setVariants] = useState([]);
  const isAdmin = ["admin", "superadmin"].includes(me.role);
  const load = useCallback(() => req("get", "/staff/menu").then(setItems), [req]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) req("get", "/staff/loyverse/meta").then((m) => setVariants(m.variants)).catch(() => {}); }, [req, isAdmin]);
  const categories = useMemo(() => [...new Set(items.map((m) => m.category))], [items]);

  const save = async (body) => {
    try {
      if (editing.id) await req("put", `/staff/menu/${editing.id}`, body);
      else await req("post", "/staff/menu", body);
      toast.success("Menu disimpan");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    try { await req("put", "/staff/menu-order", { ids: next.map((m) => m.id) }); } catch (e) { toast.error(errMsg(e)); load(); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Padam ${m.name}?`)) return;
    try { await req("delete", `/staff/menu/${m.id}`); toast.success("Menu dipadam"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resetAll = async () => {
    if (window.prompt(`Padam SEMUA ${items.length} menu (termasuk gambar)? Pesanan & jualan lama tidak terjejas.\nTaip RESET untuk sahkan.`) !== "RESET") return;
    try { const r = await req("post", "/superadmin/menu/reset"); toast.success(`${r.deleted} menu dipadam`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rise">
      <div className="flex items-center justify-between gap-2 mb-5">
        <h2 className="font-display text-2xl font-bold">Menu</h2>
        <div className="flex items-center gap-2">
          {me.role === "superadmin" && <button onClick={resetAll} disabled={!items.length} data-testid="menu-reset-all-button" className="flex items-center gap-2 px-4 h-10 rounded-full border border-red-300 text-red-700 bg-white hover:bg-red-50 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-50"><RotateCcw className="w-4 h-4" />Reset Semua Menu</button>}
          {isAdmin && <button onClick={() => setEditing({})} data-testid="menu-add-button" className="flex items-center gap-2 px-4 h-10 rounded-full bg-[#B91C1C] text-white text-sm font-semibold active:scale-95 transition-transform"><Plus className="w-4 h-4" />Tambah Menu</button>}
        </div>
      </div>
      {isAdmin && <LoyverseBar req={req} onDone={load} />}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((m, i) => (
          <div key={m.id} className="bg-white rounded-2xl border overflow-hidden flex" data-testid={`admin-menu-${m.id}`}>
            <img src={m.image || "/img/logo.png"} alt="" className={`w-28 h-full ${m.image ? "object-cover" : "object-contain p-3 bg-amber-50"}`} />
            <div className="p-4 flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className="font-display font-bold leading-tight">{m.name}</p>
                <span className="flex gap-1 shrink-0">
                  {m.show_in_app === false && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`menu-pos-only-${m.id}`}>POS sahaja</span>}
                  {!m.available && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200">Habis</span>}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{m.category} · {m.options.length} pilihan</p>
              <p className="font-bold text-[#B91C1C] mt-1">{rm(m.price)}</p>
              <p className={`text-[11px] mt-1 ${m.loyverse_variant_id ? "text-green-600" : "text-slate-400"}`}>{m.loyverse_variant_id ? "Dipadankan Loyverse" : "Belum padan Loyverse"}</p>
              {isAdmin && (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => setEditing(m)} data-testid={`menu-edit-${m.id}`} className="p-1.5 rounded border hover:bg-slate-50"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(m)} data-testid={`menu-delete-${m.id}`} className="p-1.5 rounded border hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Naik" data-testid={`menu-up-${m.id}`} className="p-1.5 rounded border hover:bg-slate-50 disabled:opacity-30 ml-auto"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Turun" data-testid={`menu-down-${m.id}`} className="p-1.5 rounded border hover:bg-slate-50 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {editing && <MenuFormDialog item={editing} variants={variants} categories={categories} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
